import React, { useCallback, useEffect, useRef, useState } from "react";
import ContactsComponent from "../Contacts/ContactsComponent.js";
import InboxMailsContainer from "../Mails/InboxMailsContainer.js";
import MobileBottomMenu from "../Popups/MobileBottomMenu.js";
import useMediaQueries from "../../../../hooks/useMediaQueries.js";
import PaginationComponent from "../Reusable/PaginationComponent.js";
import PickerMailsContainer from "../Picker/PickerMailsContainer.tsx";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import { usePicker } from "contexts/pickerContext/pickerContext.ts";
import { useInView } from "react-intersection-observer";
import useInbox from "hooks/useInbox.ts";
import AdditionalSearchInfo from "./AdditionalSearchInfo/index.tsx";
import { useAppSelector } from "store/hooks.ts";
import {
  selectTParam,
  selectPickerSearchParams,
  selectItemsPerPage,
} from "store/selectors/inboxSelectors.ts";

const InfinityScrollElement = () => {
  const { incPageNumber, getCurrentPage, getMailsQuantity, getIsFetching } =
    useInbox();
  const {
    incPageNumber: incPageNumberPicker,
    getCurrentPage: getCurrentPagePicker,
    getProjectsQuantity,
    getIsFetchingPickerData,
  } = usePicker();
  const ref = useRef();
  const [inViewRef, inView] = useInView({ threshold: 0.5 });
  const pageType = useAppSelector(selectTParam);
  const pickerSearchParams = useAppSelector(selectPickerSearchParams);
  const itemsPerPage = useAppSelector(selectItemsPerPage);
  const hasMore = () => {
    if (pageType === "picker") {
      const selectedCategoryTotal =
        pickerSearchParams.p === "archived"
          ? getProjectsQuantity()?.totalArchive
          : getProjectsQuantity()?.totalActive;
      return selectedCategoryTotal > getCurrentPagePicker() * itemsPerPage;
    } else {
      return getMailsQuantity() > getCurrentPage() * itemsPerPage;
    }
  };

  const incrementPageNumber = () => {
    if (pageType === "picker") {
      incPageNumberPicker();
    } else {
      incPageNumber();
    }
  };

  const getIsFetchingData = () => {
    if (pageType === "picker") {
      return getIsFetchingPickerData();
    } else {
      getIsFetching();
    }
  };

  // Use `useCallback` so we don't recreate the function on each render - Could result in infinite loop
  const setRefs = useCallback(
    (node) => {
      ref.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node);
    },
    [inViewRef]
  );

  useEffect(() => {
    inView && hasMore() && incrementPageNumber();
  }, [inView]);

  return hasMore() ? (
    <div className="infinity-scroll">
      {getIsFetchingData() ? (
        <LoadingImageElement />
      ) : (
        <div ref={setRefs}></div>
      )}
    </div>
  ) : null;
};

const InboxContentContainer = ({ setShowSideBar }) => {
  const [listView, setListView] = useState(true);
  const pageType = useAppSelector(selectTParam);

  const returnElemObj = {
    contacts: <ContactsComponent listView={listView} />,
    sent: (
      <InboxMailsContainer
        listView={listView}
        setShowSideBar={setShowSideBar}
      />
    ),
    received: (
      <InboxMailsContainer
        listView={listView}
        setShowSideBar={setShowSideBar}
      />
    ),
    all: (
      <InboxMailsContainer
        listView={listView}
        setShowSideBar={setShowSideBar}
        all
      />
    ),
    trash: (
      <InboxMailsContainer
        listView={listView}
        setShowSideBar={setShowSideBar}
        trash
      />
    ),
    picker: <PickerMailsContainer />,
  };

  return returnElemObj[pageType];
};

const InboxMainContainer = () => {
  const { getIsFetching, getCurrentPage } = useInbox();
  const [showSideBar, setShowSideBar] = useState(false);
  const { desktopCondition, mobileCondition } = useMediaQueries();
  const { getSearchParams } = useInbox();
  const { getIsFetchingPickerData } = usePicker();
  const pageType = useAppSelector(selectTParam);

  return (
    <>
      <div
        className="inbox-mails-container"
        id="inbox-mails-container"
        tabIndex={1}
      >
        {((pageType === "picker" && getIsFetchingPickerData()) ||
          getIsFetching()) &&
        (desktopCondition || getCurrentPage() === 1) ? (
          <div className="pagination-loader">
            <LoadingImageElement />
          </div>
        ) : (
          <>
            <InboxContentContainer setShowSideBar={setShowSideBar} />
            {mobileCondition ? <InfinityScrollElement /> : null}
          </>
        )}
        {getSearchParams()?.q ? <AdditionalSearchInfo /> : null}
        {desktopCondition ? <PaginationComponent isBottom /> : null}
        <MobileBottomMenu {...{ showSideBar, setShowSideBar }} />
      </div>
    </>
  );
};

export default InboxMainContainer;
